import { IObservableArray } from "azure-devops-ui/Core/Observable";
import React from "react";
import { noop } from "../../common/utilities/func";

export interface ISearchSetting {
  recentSearches?: string[];
}

export interface ISearchContext {
  addSearch: (searchText: string) => void;
  clearSearches: () => void;
  getSearches(): Promise<string[]>;
  removeSearch: (searchText: string) => void;
}

export const SearchContext = React.createContext<ISearchContext>({
  addSearch: noop,
  clearSearches: noop,
  getSearches: () => Promise.resolve([]),
  removeSearch: noop
});

export class PhotoSearchContext implements ISearchContext {
  private recentSearches: IObservableArray<string>;

  /**
   * Creates a new instance of the PhotoSearchContext.
   *
   * @param recentSearches The observable array of recent searches.
   */
  constructor(recentSearches: IObservableArray<string>) {
    this.recentSearches = recentSearches;
  }

  /**
   * Adds a search to the searches array. If the search already exists, it will
   * be removed and added to the top of the list. If the list is longer than 6
   * items, the last item will be removed.
   *
   * @param searchText The search to be added.
   */
  public addSearch(searchText: string) {
    // Do all the updates in a local array so to avoid triggering multiple updates.
    const updatedSearches = [...this.recentSearches.value];

    // Do a case insensitive comparison to existing searches and remove any
    // duplicate that exist.
    for (let searchIndex = 0; searchIndex < updatedSearches.length; searchIndex++) {
      if (searchText.localeCompare(updatedSearches[searchIndex], undefined, { sensitivity: "base" }) === 0) {
        updatedSearches.splice(searchIndex, 1);
        break;
      }
    }

    // Prepend the new search and ensure we only track the latest 6.
    updatedSearches.unshift(searchText);
    updatedSearches.splice(6, 1);

    // Update the observable array of searches with the updated list.
    this.recentSearches.splice(0, this.recentSearches.length, ...updatedSearches);
  }

  /**
   * Clears the recent searches array
   */
  public clearSearches() {
    this.recentSearches.splice(0, this.recentSearches.length);
  }

  /**
   * Returns the searches array.
   *
   * @return A promise to the string[] of searches.
   */
  public getSearches(): Promise<string[]> {
    return Promise.resolve(this.recentSearches.value);
  }

  /**
   * Removes a specified search from the searches array.
   *
   * @param searchText The search to remove.
   */
  public removeSearch(searchText: string) {
    const searchIndex = this.recentSearches.value.indexOf(searchText);

    if (searchIndex !== -1) {
      this.recentSearches.splice(searchIndex, 1);
    }
  }
}
